import React, { useState, useEffect, useMemo } from "react";
import { getData, postData, putData, deleteData } from "../../services/api.service";
import { toastSuccess, toastError } from "../common/Toast";
import {
  FaPalette,
  FaLayerGroup,
  FaTags,
  FaPlus,
  FaEdit,
  FaTrash,
  FaSave,
  FaExclamationTriangle
} from "react-icons/fa";

function MaterialsManager() {
  const [materials, setMaterials] = useState([]);
  const [materialTypes, setMaterialTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('materials'); // 'materials' o 'types'
  const [showInactiveMaterials, setShowInactiveMaterials] = useState(false);
  const [showInactiveTypes, setShowInactiveTypes] = useState(false);
  
  // Estados para materiales
  const [showMaterialModal, setShowMaterialModal] = useState(false);
  const [editingMaterial, setEditingMaterial] = useState(null);
  const [materialFormData, setMaterialFormData] = useState({
    material_type_id: '',
    material_name: '',
    active: 1
  });

  // Estados para tipos de material
  const [showTypeModal, setShowTypeModal] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [typeFormData, setTypeFormData] = useState({
    material_type_name: '',
    active: 1
  });

  // Estados para eliminación permanente de materiales
  const [showPermanentDeleteMaterialModal, setShowPermanentDeleteMaterialModal] = useState(false);
  const [permanentDeletingMaterial, setPermanentDeletingMaterial] = useState(null);

  // Estados para eliminación permanente de tipos
  const [showPermanentDeleteTypeModal, setShowPermanentDeleteTypeModal] = useState(false);
  const [permanentDeletingType, setPermanentDeletingType] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [materialResponse, typeResponse] = await Promise.all([
        getData('/api/admin/get_materials'),
        getData('/api/admin/get_material_types')
      ]);
      
      setMaterials(materialResponse.data.materials || []);
      setMaterialTypes(typeResponse.data.materialTypes || []);
    } catch (error) {
      console.error('Error cargando datos:', error);
      toastError('Error al cargar los datos de materiales');
    } finally {
      setLoading(false);
    }
  };

  const loadMaterialsOnly = async () => {
    try {
      const response = await getData('/api/admin/get_materials');
      setMaterials(response.data.materials || []);
    } catch (error) {
      console.error('Error recargando materiales:', error);
    }
  };

  const loadTypesOnly = async () => {
    try {
      const response = await getData('/api/admin/get_material_types');
      setMaterialTypes(response.data.materialTypes || []);
    } catch (error) {
      console.error('Error recargando tipos:', error);
    }
  };

  const handleMaterialSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      
      const dataToSend = {
        data: {
          material_name: materialFormData.material_name,
          material_type_id: parseInt(materialFormData.material_type_id),
          active: materialFormData.active,
          user: 1 // TODO: Obtener del contexto de usuario actual
        }
      };
      
      if (editingMaterial) {
        dataToSend.data.id = editingMaterial.id;
        
        const response = await putData('/api/admin/update_material', dataToSend);
        
        // Verificar si la respuesta es un error
        if (response.response && response.response.status >= 400) {
          throw new Error(response.response.data?.msg || 'Error al actualizar');
        }
        
        // Actualización optimista: actualizar el item en la lista local
        setMaterials(prevMaterials => prevMaterials.map(material => 
          material.id === editingMaterial.id 
            ? {
                ...material,
                material_name: materialFormData.material_name,
                material_type_id: parseInt(materialFormData.material_type_id),
                material_type_name: materialTypes.find(t => t.id === parseInt(materialFormData.material_type_id))?.material_type_name || material.material_type_name,
                active: materialFormData.active
              }
            : material
        ));
        
        toastSuccess(`Material "${materialFormData.material_name}" actualizado exitosamente`);
      } else {
        const response = await postData('/api/admin/create_material', dataToSend);
        
        // Verificar si la respuesta es un error
        if (response.response && response.response.status >= 400) {
          throw new Error(response.response.data?.msg || 'Error al crear');
        }
        
        // Actualización optimista al crear
        if (response.data?.material) {
          const newMaterial = {
            id: response.data.material.id || response.data.id,
            material_name: materialFormData.material_name,
            material_type_id: parseInt(materialFormData.material_type_id),
            material_type_name: materialTypes.find(t => t.id === parseInt(materialFormData.material_type_id))?.material_type_name || '',
            active: materialFormData.active
          };
          setMaterials(prev => [newMaterial, ...prev]);
        }
        
        toastSuccess(`Material "${materialFormData.material_name}" creado exitosamente`);
        loadMaterialsOnly().catch(() => {});
      }
      
      setShowMaterialModal(false);
      resetMaterialForm();
    } catch (error) {
      console.error('Error guardando material:', error);
      toastError(error.message || (editingMaterial ? 'Error al actualizar el material' : 'Error al crear el material'));
    } finally {
      setLoading(false);
    }
  };

  const resetMaterialForm = () => {
    setEditingMaterial(null);
    setMaterialFormData({
      material_type_id: '',
      material_name: '',
      active: 1
    });
  };

  const handleEditMaterial = (material) => {
    setEditingMaterial(material);
    setMaterialFormData({
      material_type_id: material.material_type_id || '',
      material_name: material.material_name || '',
      active: material.active !== undefined ? material.active : 1
    });
    setShowMaterialModal(true);
  };

  const handleNewMaterial = () => {
    setEditingMaterial(null);
    setMaterialFormData({
      material_type_id: '',
      material_name: '',
      active: 1
    });
    setShowMaterialModal(true);
  };

  const handleMaterialInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setMaterialFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // ======= FUNCIONES PARA TIPOS DE MATERIAL =======
  const handleTypeSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      
      const dataToSend = {
        data: {
          material_type_name: typeFormData.material_type_name,
          active: typeFormData.active,
          user: 1 // TODO: Obtener del contexto de usuario actual
        }
      };
      
      if (editingType) {
        dataToSend.data.id = editingType.id;
        
        const response = await putData('/api/admin/update_material_type', dataToSend);
        
        // Verificar si la respuesta es un error
        if (response.response && response.response.status >= 400) {
          throw new Error(response.response.data?.msg || 'Error al actualizar');
        }
        
        // Actualización optimista
        setMaterialTypes(prevTypes => prevTypes.map(type => 
          type.id === editingType.id 
            ? {
                ...type,
                material_type_name: typeFormData.material_type_name,
                active: typeFormData.active
              }
            : type
        ));
        
        toastSuccess(`Tipo de material "${typeFormData.material_type_name}" actualizado exitosamente`);
      } else {
        const response = await postData('/api/admin/create_material_type', dataToSend);
        
        // Verificar si la respuesta es un error
        if (response.response && response.response.status >= 400) {
          throw new Error(response.response.data?.msg || 'Error al crear');
        }
        
        // Actualización optimista al crear tipo
        if (response.data?.materialType) {
          const newType = {
            id: response.data.materialType.id || response.data.id,
            material_type_name: typeFormData.material_type_name,
            active: typeFormData.active
          };
          setMaterialTypes(prev => [newType, ...prev]);
        }
        
        toastSuccess(`Tipo de material "${typeFormData.material_type_name}" creado exitosamente`);
        loadTypesOnly().catch(() => {});
      }
      
      setShowTypeModal(false);
      resetTypeForm();
    } catch (error) {
      console.error('Error guardando tipo de material:', error);
      toastError(error.message || (editingType ? 'Error al actualizar el tipo de material' : 'Error al crear el tipo de material'));
    } finally {
      setLoading(false);
    }
  };

  const resetTypeForm = () => {
    setEditingType(null);
    setTypeFormData({
      material_type_name: '',
      active: 1
    });
  };

  const handleEditType = (type) => {
    setEditingType(type);
    setTypeFormData({
      material_type_name: type.material_type_name || '',
      active: type.active !== undefined ? type.active : 1
    });
    setShowTypeModal(true);
  };

  const handleNewType = () => {
    setEditingType(null);
    setTypeFormData({
      material_type_name: '',
      active: 1
    });
    setShowTypeModal(true);
  };

  const handleTypeInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setTypeFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // ======= FUNCIONES DE ELIMINACIÓN =======
  const handleDeleteMaterial = async (material) => {
    if (window.confirm(`¿Está seguro que desea desactivar el material "${material.material_name}"?`)) {
      try {
        setLoading(true);
        
        // No eliminar físicamente, solo marcar como inactivo (soft delete)
        const dataToSend = {
          data: {
            id: material.id,
            material_name: material.material_name,
            material_type_id: material.material_type_id,
            active: 0, // Marcar como inactivo
            user: 1 // TODO: Obtener del contexto de usuario actual
          }
        };
        
        const response = await putData('/api/admin/update_material', dataToSend);
        
        // Verificar si la respuesta es un error
        if (response.response && response.response.status >= 400) {
          throw new Error(response.response.data?.msg || 'Error al desactivar');
        }
        
        // Actualización optimista: marcar como inactivo en la lista
        setMaterials(prevMaterials => prevMaterials.map(m => 
          m.id === material.id ? { ...m, active: 0 } : m
        ));
        
        toastSuccess(`Material "${material.material_name}" desactivado exitosamente`);
      } catch (error) {
        console.error('Error desactivando material:', error);
        toastError(error.message || 'Error al desactivar el material');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleDeleteType = async (type) => {
    if (window.confirm(`¿Está seguro que desea desactivar el tipo de material "${type.material_type_name}"?`)) {
      try {
        setLoading(true);
        
        // No eliminar físicamente, solo marcar como inactivo (soft delete)
        const dataToSend = {
          data: {
            id: type.id,
            material_type_name: type.material_type_name,
            active: 0, // Marcar como inactivo
            user: 1 // TODO: Obtener del contexto de usuario actual
          }
        };
        
        const response = await putData('/api/admin/update_material_type', dataToSend);
        
        // Verificar si la respuesta es un error
        if (response.response && response.response.status >= 400) {
          throw new Error(response.response.data?.msg || 'Error al desactivar');
        }
        
        // Actualización optimista: marcar como inactivo en la lista
        setMaterialTypes(prevTypes => prevTypes.map(t => 
          t.id === type.id ? { ...t, active: 0 } : t
        ));
        
        toastSuccess(`Tipo de material "${type.material_type_name}" desactivado exitosamente`);
      } catch (error) {
        console.error('Error desactivando tipo de material:', error);
        toastError(error.message || 'Error al desactivar el tipo de material');
      } finally {
        setLoading(false);
      }
    }
  };

  // Funciones de eliminación PERMANENTE de materiales
  const handlePermanentDeleteMaterial = (material) => {
    setPermanentDeletingMaterial(material);
    setShowPermanentDeleteMaterialModal(true);
  };

  const confirmPermanentDeleteMaterial = async () => {
    if (!permanentDeletingMaterial) return;
    
    try {
      setLoading(true);
      
      const dataToSend = {
        id: permanentDeletingMaterial.id,
        user: 1
      };
      
      const response = await deleteData('/api/admin/delete_material', dataToSend);
      
      if (response?.response && response.response.status >= 400) {
        throw new Error(response.response.data?.msg || 'Error al eliminar');
      }
      
      // Actualización optimista: eliminar de la lista
      setMaterials(prevMaterials => prevMaterials.filter(material => material.id !== permanentDeletingMaterial.id));
      
      toastSuccess(`Material "${permanentDeletingMaterial.material_name}" eliminado permanentemente`);
      setShowPermanentDeleteMaterialModal(false);
      setPermanentDeletingMaterial(null);
    } catch (error) {
      console.error('Error eliminando material:', error);
      toastError(error.message || 'Error al eliminar el material definitivamente');
    } finally {
      setLoading(false);
    }
  };

  const cancelPermanentDeleteMaterial = () => {
    setShowPermanentDeleteMaterialModal(false);
    setPermanentDeletingMaterial(null);
  };

  // Funciones de eliminación PERMANENTE de tipos
  const handlePermanentDeleteType = (type) => {
    setPermanentDeletingType(type);
    setShowPermanentDeleteTypeModal(true);
  };

  const confirmPermanentDeleteType = async () => {
    if (!permanentDeletingType) return;
    
    try {
      setLoading(true);
      
      const dataToSend = {
        id: permanentDeletingType.id,
        user: 1
      };
      
      const response = await deleteData('/api/admin/delete_material_type', dataToSend);
      
      if (response?.response && response.response.status >= 400) {
        throw new Error(response.response.data?.msg || 'Error al eliminar');
      }
      
      // Actualización optimista: eliminar de la lista
      setMaterialTypes(prevTypes => prevTypes.filter(type => type.id !== permanentDeletingType.id));
      
      toastSuccess(`Tipo de material "${permanentDeletingType.material_type_name}" eliminado permanentemente`);
      setShowPermanentDeleteTypeModal(false);
      setPermanentDeletingType(null);
    } catch (error) {
      console.error('Error eliminando tipo de material:', error);
      toastError(error.message || 'Error al eliminar el tipo de material definitivamente');
    } finally {
      setLoading(false);
    }
  };

  const cancelPermanentDeleteType = () => {
    setShowPermanentDeleteTypeModal(false);
    setPermanentDeletingType(null);
  };

  // Filtrar materiales y tipos según los toggles - Optimizado con useMemo
  const filteredMaterials = useMemo(() => {
    return showInactiveMaterials ? materials : materials.filter(material => material.active);
  }, [materials, showInactiveMaterials]);

  const filteredMaterialTypes = useMemo(() => {
    return showInactiveTypes ? materialTypes : materialTypes.filter(type => type.active);
  }, [materialTypes, showInactiveTypes]);

  return (
    <div className="materials-manager">
      <div className="card">
        <div className="card-header">
          <h5 className="card-title mb-3">
            <FaPalette className="me-2 text-primary" />
            Gestión de Materiales
          </h5>
          
          {/* Pestañas de navegación */}
          <ul className="nav nav-tabs card-header-tabs">
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'materials' ? 'active' : ''}`}
                onClick={() => setActiveTab('materials')}
                disabled={loading}
              >
                <FaLayerGroup className="me-2" />
                Materiales
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'types' ? 'active' : ''}`}
                onClick={() => setActiveTab('types')}
                disabled={loading}
              >
                <FaTags className="me-2" />
                Tipos de Material
              </button>
            </li>
          </ul>
        </div>

        <div className="card-body">
          {/* Contenido de la pestaña Materiales */}
          {activeTab === 'materials' && (
            <>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="mb-0">Lista de Materiales</h6>
                <div className="d-flex gap-2 align-items-center">
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="showInactiveMaterialsToggle"
                      checked={showInactiveMaterials}
                      onChange={(e) => setShowInactiveMaterials(e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="showInactiveMaterialsToggle">
                      Mostrar inactivos
                    </label>
                  </div>
                  <button
                    className="btn btn-primary"
                    onClick={handleNewMaterial}
                    disabled={loading}
                  >
                    <FaPlus className="me-2" />
                    Nuevo Material
                  </button>
                </div>
              </div>

              {!showInactiveMaterials && materials.length > filteredMaterials.length && (
                <div className="alert alert-info d-flex justify-content-between align-items-center mb-3">
                  <div>
                    <i className="fas fa-info-circle me-2"></i>
                    Hay <strong>{materials.length - filteredMaterials.length}</strong> elemento(s) inactivo(s) oculto(s).
                  </div>
                  <button 
                    className="btn btn-sm btn-outline-primary"
                    onClick={() => setShowInactiveMaterials(true)}
                  >
                    Mostrar todos
                  </button>
                </div>
              )}

              {loading ? (
                <div className="text-center py-4">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Cargando...</span>
                  </div>
                  <div className="mt-2 text-muted">Cargando materiales...</div>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Nombre</th>
                        <th>Tipo</th>
                        <th>Estado</th>
                        <th>Fecha Creación</th>
                        <th className="text-center">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredMaterials.length === 0 ? (
                        <tr>
                          <td colSpan="6" className="text-center text-muted py-4">
                            {showInactiveMaterials ? 'No hay materiales registrados' : 'No hay materiales activos. Activa el toggle "Mostrar inactivos" para ver materiales eliminados.'}
                          </td>
                        </tr>
                      ) : (
                        filteredMaterials.map(material => (
                          <tr key={material.id}>
                            <td>#{material.id}</td>
                            <td>
                              <strong>{material.material_name}</strong>
                            </td>
                            <td>
                              <span className="badge bg-info">
                                {material.material_type_name}
                              </span>
                            </td>
                            <td>
                              <span className={`badge ${material.active ? 'bg-success' : 'bg-danger'}`}>
                                {material.active ? 'Activo' : 'Inactivo'}
                              </span>
                            </td>
                            <td>
                              <small className="text-muted">
                                {new Date(material.inserted_date).toLocaleDateString('es-AR')}
                              </small>
                            </td>
                            <td className="text-center">
                              <div className="btn-group" role="group">
                                <button
                                  className="btn btn-sm btn-outline-primary"
                                  onClick={() => handleEditMaterial(material)}
                                  disabled={loading}
                                  title="Editar material"
                                >
                                  <FaEdit />
                                </button>
                                <button
                                  className="btn btn-sm btn-outline-warning"
                                  onClick={() => handleDeleteMaterial(material)}
                                  disabled={loading}
                                  title={material.active ? "Inactivar material" : "Activar material"}
                                >
                                  <FaTrash />
                                </button>
                                <button
                                  className="btn btn-sm btn-outline-danger"
                                  onClick={() => handlePermanentDeleteMaterial(material)}
                                  disabled={loading}
                                  title="Eliminar definitivamente (irreversible)"
                                >
                                  <FaExclamationTriangle />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}

          {/* Contenido de la pestaña Tipos de Material */}
          {activeTab === 'types' && (
            <>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="mb-0">Tipos de Material</h6>
                <div className="d-flex gap-2 align-items-center">
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="showInactiveTypesToggle"
                      checked={showInactiveTypes}
                      onChange={(e) => setShowInactiveTypes(e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="showInactiveTypesToggle">
                      Mostrar inactivos
                    </label>
                  </div>
                  <button
                    className="btn btn-primary"
                    onClick={handleNewType}
                    disabled={loading}
                  >
                    <FaPlus className="me-2" />
                    Nuevo Tipo
                  </button>
                </div>
              </div>

              {!showInactiveTypes && materialTypes.length > filteredMaterialTypes.length && (
                <div className="alert alert-info d-flex justify-content-between align-items-center mb-3">
                  <div>
                    <i className="fas fa-info-circle me-2"></i>
                    Hay <strong>{materialTypes.length - filteredMaterialTypes.length}</strong> elemento(s) inactivo(s) oculto(s).
                  </div>
                  <button 
                    className="btn btn-sm btn-outline-primary"
                    onClick={() => setShowInactiveTypes(true)}
                  >
                    Mostrar todos
                  </button>
                </div>
              )}

              {loading ? (
                <div className="text-center py-4">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Cargando...</span>
                  </div>
                  <div className="mt-2 text-muted">Cargando tipos...</div>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Nombre del Tipo</th>
                        <th>Estado</th>
                        <th>Fecha Creación</th>
                        <th>Cantidad de Materiales</th>
                        <th className="text-center">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredMaterialTypes.length === 0 ? (
                        <tr>
                          <td colSpan="6" className="text-center text-muted py-4">
                            {showInactiveTypes ? 'No hay tipos de material registrados' : 'No hay tipos activos. Activa el toggle "Mostrar inactivos" para ver tipos eliminados.'}
                          </td>
                        </tr>
                      ) : (
                        filteredMaterialTypes.map(type => (
                          <tr key={type.id}>
                            <td>#{type.id}</td>
                            <td>
                              <strong>{type.material_type_name}</strong>
                            </td>
                            <td>
                              <span className={`badge ${type.active ? 'bg-success' : 'bg-danger'}`}>
                                {type.active ? 'Activo' : 'Inactivo'}
                              </span>
                            </td>
                            <td>
                              <small className="text-muted">
                                {new Date(type.inserted_date).toLocaleDateString('es-AR')}
                              </small>
                            </td>
                            <td>
                              <span className="badge bg-secondary">
                                {materials.filter(m => m.material_type_id === type.id).length} materiales
                              </span>
                            </td>
                            <td className="text-center">
                              <div className="btn-group" role="group">
                                <button
                                  className="btn btn-sm btn-outline-primary"
                                  onClick={() => handleEditType(type)}
                                  disabled={loading}
                                  title="Editar tipo"
                                >
                                  <FaEdit />
                                </button>
                                <button
                                  className="btn btn-sm btn-outline-warning"
                                  onClick={() => handleDeleteType(type)}
                                  disabled={loading}
                                  title={type.active ? "Inactivar tipo" : "Activar tipo"}
                                >
                                  <FaTrash />
                                </button>
                                <button
                                  className="btn btn-sm btn-outline-danger"
                                  onClick={() => handlePermanentDeleteType(type)}
                                  disabled={loading}
                                  title="Eliminar definitivamente (irreversible)"
                                >
                                  <FaExclamationTriangle />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Modal para crear/editar material */}
      {showMaterialModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {editingMaterial ? 'Editar Material' : 'Nuevo Material'}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowMaterialModal(false)}
                  disabled={loading}
                ></button>
              </div>
              
              <form onSubmit={handleMaterialSubmit}>
                <div className="modal-body">
                  <div className="row">
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Tipo de Material *</label>
                        <select
                          className="form-select"
                          name="material_type_id"
                          value={materialFormData.material_type_id}
                          onChange={handleMaterialInputChange}
                          required
                          disabled={loading}
                        >
                          <option value="">Seleccionar tipo</option>
                          {materialTypes.map(type => (
                            <option key={type.id} value={type.id}>
                              {type.material_type_name}
                            </option>
                          ))}
                        </select>
                      </div>
                      
                      <div className="mb-3">
                        <label className="form-label">Nombre del Material *</label>
                        <input
                          type="text"
                          className="form-control"
                          name="material_name"
                          value={materialFormData.material_name}
                          onChange={handleMaterialInputChange}
                          required
                          disabled={loading}
                          placeholder="Ej: PANA, TUXOR, LINO, etc."
                        />
                      </div>
                    </div>
                    
                    <div className="col-md-6">
                      <div className="mb-3">
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            name="active"
                            checked={materialFormData.active === 1}
                            onChange={(e) => setMaterialFormData(prev => ({
                              ...prev,
                              active: e.target.checked ? 1 : 0
                            }))}
                            disabled={loading}
                          />
                          <label className="form-check-label">
                            Material activo
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowMaterialModal(false)}
                    disabled={loading}
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={loading || !materialFormData.material_name.trim() || !materialFormData.material_type_id}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        Guardando...
                      </>
                    ) : (
                      <>
                        <FaSave className="me-2" />
                        {editingMaterial ? 'Actualizar' : 'Crear'}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal para crear/editar tipo de material */}
      {showTypeModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {editingType ? 'Editar Tipo de Material' : 'Nuevo Tipo de Material'}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowTypeModal(false)}
                  disabled={loading}
                ></button>
              </div>
              
              <form onSubmit={handleTypeSubmit}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Nombre del Tipo de Material *</label>
                    <input
                      type="text"
                      className="form-control"
                      name="material_type_name"
                      value={typeFormData.material_type_name}
                      onChange={handleTypeInputChange}
                      required
                      disabled={loading}
                      placeholder="Ej: TELA, CUERO, MADERA, METAL, etc."
                    />
                    <div className="form-text">
                      Ejemplos: TELA, CUERO, MADERA, METAL, PLÁSTICO, VIDRIO
                    </div>
                  </div>
                  
                  <div className="mb-3">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        name="active"
                        checked={typeFormData.active === 1}
                        onChange={(e) => setTypeFormData(prev => ({
                          ...prev,
                          active: e.target.checked ? 1 : 0
                        }))}
                        disabled={loading}
                      />
                      <label className="form-check-label">
                        Tipo activo
                      </label>
                    </div>
                  </div>
                </div>
                
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowTypeModal(false)}
                    disabled={loading}
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={loading || !typeFormData.material_type_name.trim()}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        Guardando...
                      </>
                    ) : (
                      <>
                        <FaSave className="me-2" />
                        {editingType ? 'Actualizar' : 'Crear'}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmación de eliminación PERMANENTE de material */}
      {showPermanentDeleteMaterialModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-danger">
              <div className="modal-header bg-danger text-white">
                <h5 className="modal-title">
                  <FaExclamationTriangle className="me-2" />
                  ⚠️ ELIMINAR DEFINITIVAMENTE
                </h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={cancelPermanentDeleteMaterial}
                  disabled={loading}
                ></button>
              </div>
              <div className="modal-body">
                <div className="alert alert-danger">
                  <strong>⚠️ ADVERTENCIA: Esta acción es IRREVERSIBLE</strong>
                </div>
                <p>¿Está COMPLETAMENTE seguro que desea eliminar permanentemente el siguiente material?</p>
                {permanentDeletingMaterial && (
                  <div className="alert alert-warning">
                    <strong>Material:</strong> {permanentDeletingMaterial.material_name}<br/>
                    <strong>Tipo:</strong> {permanentDeletingMaterial.material_type_name}
                  </div>
                )}
                <ul className="text-danger">
                  <li>El material se eliminará <strong>permanentemente</strong> de la base de datos</li>
                  <li>NO se podrá recuperar esta información</li>
                  <li>Puede afectar pedidos y referencias existentes</li>
                </ul>
                <p className="mt-3">
                  <small className="text-muted">
                    💡 <strong>Recomendación:</strong> Si solo desea ocultar temporalmente este material,
                    use el botón de "Inactivar" en su lugar.
                  </small>
                </p>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={cancelPermanentDeleteMaterial}
                  disabled={loading}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={confirmPermanentDeleteMaterial}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Eliminando...
                    </>
                  ) : (
                    <>
                      <FaExclamationTriangle className="me-2" />
                      ⚠️ SÍ, ELIMINAR DEFINITIVAMENTE
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmación de eliminación PERMANENTE de tipo */}
      {showPermanentDeleteTypeModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-danger">
              <div className="modal-header bg-danger text-white">
                <h5 className="modal-title">
                  <FaExclamationTriangle className="me-2" />
                  ⚠️ ELIMINAR DEFINITIVAMENTE
                </h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={cancelPermanentDeleteType}
                  disabled={loading}
                ></button>
              </div>
              <div className="modal-body">
                <div className="alert alert-danger">
                  <strong>⚠️ ADVERTENCIA: Esta acción es IRREVERSIBLE</strong>
                </div>
                <p>¿Está COMPLETAMENTE seguro que desea eliminar permanentemente el siguiente tipo de material?</p>
                {permanentDeletingType && (
                  <div className="alert alert-warning">
                    <strong>Tipo:</strong> {permanentDeletingType.material_type_name}
                  </div>
                )}
                <ul className="text-danger">
                  <li>El tipo de material se eliminará <strong>permanentemente</strong> de la base de datos</li>
                  <li>NO se podrá recuperar esta información</li>
                  <li>Puede afectar materiales y referencias existentes</li>
                </ul>
                <p className="mt-3">
                  <small className="text-muted">
                    💡 <strong>Recomendación:</strong> Si solo desea ocultar temporalmente este tipo,
                    use el botón de "Inactivar" en su lugar.
                  </small>
                </p>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={cancelPermanentDeleteType}
                  disabled={loading}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={confirmPermanentDeleteType}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Eliminando...
                    </>
                  ) : (
                    <>
                      <FaExclamationTriangle className="me-2" />
                      ⚠️ SÍ, ELIMINAR DEFINITIVAMENTE
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MaterialsManager;